<template>
  <BaseModule
    v-bind="$props"
    class="container promo-single"
    data-module="promo-single"
  >
    <div class="content">
      <header
        v-if="title || subtitle"
        class="base-title-fontsize"
      >
        <component
          :is="titleTag"
          v-if="title"
          class="tit tit-g"
        >
          {{ title }}
        </component>
        <h3
          v-if="subtitle"
          class="sub"
        >
          {{ subtitle }}
        </h3>
      </header>

      <Warning
        v-if="editing"
        text="O módulo não possui um hotel selecionado"
      >
      </Warning>

      <Loader v-else-if="loading" />

      <div
        v-else
        class="lista-promocoes"
      >
        <EmptyState
          v-if="isEmptyState"
          :title="noPromoTitle"
          :subtitle="noPromoSubtitle"
          :img="promoImg"
        />

        <div
          v-for="p in promoVisibleList"
          :key="p.Id"
          :data-promo="p.Id"
          class="bloco-promocao"
        >
          <div class="imagem">
            <LImage
              :class="{ 'empty-image': !hasImage(p) }"
              :src="getPromoImage(p)"
              :alt="p.Nome"
            />
            <div
              v-if="hasDiscount(p)"
              class="promo-label"
            >
              {{ $tc('discount', p.Tarifa.PercentualDesconto) }}
            </div>
          </div>

          <div class="informacoes">
            <div class="descricao">
              <div class="header">
                <h4 class="nome">
                  {{ p.Nome }}
                </h4>

                <div
                  class="info-tooltip"
                  @mouseover="showTooltip"
                  @mouseleave="hideTooltip"
                >
                  <IconHelp />
                  <div class="slb-tooltip arrow-right-down">
                    <ul class="items-tooltip">
                      <li>{{ p.ItensDescricao.TipoPagamento }}</li>
                      <li>{{ p.ItensDescricao.Reembolsavel }}</li>
                      <li>{{ p.ItensDescricao.Pensao }}</li>
                      <li>{{ p.ItensDescricao.MinimoNoites }}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div
                v-if="hasDescription(p)"
                class="promo-desc-container"
              >
                <div class="features custom-scrollbar">
                  <p class="descricaoLastMinute">
                    {{ p.Descricao }}
                  </p>
                  <p class="descricaoPrincipal">
                    {{ p.DescricaoLastMinute }}
                  </p>
                </div>
              </div>
            </div>

            <div class="tarifas">
              <p
                v-if="showPrice"
                class="diaria"
              >
                <span class="rotulo">{{ $t('dailyRate') }} </span>
                <span
                  v-if="hasDiscount(p)"
                  class="valor-promo"
                >
                  {{ p.Tarifa.ValorFormatadoSemDesconto }}
                </span>
                <span
                  class="valor"
                  :style="{ color: getValueColor(p) }"
                >
                  {{ p.Tarifa.ValorFormatado }}
                </span>
              </p>
            </div>
          </div>
          <EditorLink
            :model="p.UrlPromocao"
            class="cta"
            @click.native="trackCustomEvent(p)"
          >
            {{ buttonText || $t('seePromotion') }}
          </EditorLink>
        </div>
      </div>

      <button
        v-if="hasPromoToLoad"
        class="cta vazado cta--ver-mais"
        @click="showMorePromo()"
      >
        {{ $t('seeMore') }}
      </button>
    </div>
  </BaseModule>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import getApiHotels from '@/mixins/getApiHotels';
import base from '@/mixins/base';
import Loader from '@/components/Loader.vue';
import Warning from '@/components/Warning.vue';
import IconHelp from '@/components/icons/IconHelp.vue';
import { pushDataLayer } from '@/helpers/gtm';
import handlePromos from '@/helpers/handlePromos';
import randomizeArray from '@/helpers/randomizeArray';
import EmptyState from './EmptyState.vue';
import EditorLink from './EditorLink.vue';
import BaseModule from './generic/BaseModule.vue';

const emptyPromoUrlPath = require('@/assets/promoOptin.png');

export default {
  name: 'PromoSingle',
  components: {
    BaseModule,
    Loader,
    Warning,
    IconHelp,
    EmptyState,
    EditorLink,
  },
  mixins: [getApiHotels, base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    customNoPromoTitle: {
      type: String,
      default: '',
    },
    customNoPromoSubtitle: {
      type: String,
      default: '',
    },
    hotel: {
      type: Number,
      default: undefined,
    },
    orderBy: {
      type: String,
      default: 'initial-date',
    },
    buttonText: {
      type: String,
      default: '',
    },
    showPrice: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: false,
      promos: [],
      visiblePromoNumber: 4,
      promoImg: emptyPromoUrlPath,
      isMobile: window.innerWidth < 640,
    };
  },
  computed: {
    editing() {
      return typeof this.$parent === 'undefined' && !this.hotel;
    },
    noPromoTitle() {
      return this.customNoPromoTitle || this.$t('notAvailable');
    },
    noPromoSubtitle() {
      return this.customNoPromoSubtitle || this.$t('notAvailableSubtitle');
    },
    hasPromo() {
      return this.promos.length > 0;
    },
    hasPromoToLoad() {
      return this.visiblePromoNumber < this.promos.length;
    },
    promoVisibleList() {
      return this.promos
        ? this.orderList(this.promos).slice(0, this.visiblePromoNumber)
        : [];
    },
    isEmptyState() {
      return !this.hasPromo && !this.loading;
    },
    ...mapState(['apiData']),
    ...mapGetters(['settings']),
  },
  watch: {
    hotel(val) {
      if (typeof val === 'number') {
        this.requestPromos();
      }
    },
  },
  mounted() {
    this.getApiHotels();

    if (this.hotel) {
      this.requestPromos();
    }
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 640;
    });
  },
  methods: {
    orderList(list) {
      const promoList = [...list];

      switch (this.orderBy) {
        case 'initial-date':
          return promoList.sort((promoA, promoB) => {
            const date1 = promoA.PeriodoVigente.Inicio;
            const date2 = promoB.PeriodoVigente.Inicio;

            if (!date1) {
              return 1;
            }
            if (!date2) {
              return -1;
            }

            return new Date(date1) - new Date(date2);
          });
        case 'lowest-price':
          return promoList.sort(
            (promoA, promoB) => promoA.Tarifa.Valor - promoB.Tarifa.Valor,
          );
        case 'highest-price':
          return promoList.sort(
            (promoA, promoB) => promoB.Tarifa.Valor - promoA.Tarifa.Valor,
          );
        case 'highest-discount':
          return promoList.sort((promoA, promoB) => {
            const discount1 = this.hasPromoDiscount(promoA)
              ? promoA.Tarifa.PercentualDesconto
              : 0;
            const discount2 = this.hasPromoDiscount(promoB)
              ? promoB.Tarifa.PercentualDesconto
              : 0;

            return discount2 - discount1;
          });
        case 'random':
          return randomizeArray(promoList);
        default:
          return promoList;
      }
    },
    hasPromoDiscount(promo) {
      const tarifa = promo.Tarifa;
      return (
        tarifa.ExibirDesconto &&
        tarifa.TemDesconto &&
        tarifa.PercentualDesconto !== 0
      );
    },
    hasDescription(promo) {
      return typeof promo.Descricao === 'string' && /\S/.test(promo.Descricao);
    },
    hasImage(promo) {
      return (
        typeof promo.Imagem?.Url === 'string' && /\S/.test(promo.Imagem?.Url)
      );
    },
    hasDiscount(promo) {
      return promo.ExibirDesconto && promo.Tarifa.PercentualDesconto !== 0;
    },
    getPromoImage(promo) {
      return (
        promo.Imagem?.Url ||
        'https://s3-sa-east-1.amazonaws.com/letslandingpage/assets/images/indisponivel.gif'
      );
    },
    getValueColor(promo) {
      return this.hasDiscount(promo)
        ? this.settings.promotionColor
        : this.settings.primaryColor;
    },
    async requestPromos() {
      this.loading = true;
      const platform = this.isMobile ? 'mobile' : 'desktop';
      const url = `${this.settings.letsbookApiId}/promocoes/${this.hotel}/${platform}/${this.apiLocale}`;

      if (!this.apiData[url]) {
        try {
          await this.$store.dispatch('loadApiData', url);
        } catch {
          this.loading = false;
          return;
        }
      }

      this.promos = handlePromos(this.apiData[url])
        .sort((promo1, promo2) => {
          const dataInicio1 = promo1.PeriodoVigente.Inicio;
          const dataInicio2 = promo2.PeriodoVigente.Inicio;

          if (!dataInicio1) {
            return 1;
          }
          if (!dataInicio2) {
            return -1;
          }
          return new Date(dataInicio1) - new Date(dataInicio2);
        })
        .filter((promo) => promo.CodigoHotel === this.hotel);
      this.loading = false;
    },
    showMorePromo() {
      this.visiblePromoNumber += 4;
    },
    showTooltip(event) {
      const el = event.currentTarget;
      const slbTooltip = el.querySelector('.slb-tooltip');
      const position = el.getBoundingClientRect();
      if (position.top < slbTooltip.clientHeight + 10) {
        slbTooltip.classList.add('open-down');
      } else {
        slbTooltip.classList.add('open-up');
      }
    },
    hideTooltip(event) {
      const el = event.currentTarget;
      const slbTooltip = el.querySelector('.slb-tooltip');
      slbTooltip.classList.remove('open-up', 'open-down');
    },
    trackCustomEvent(promo) {
      pushDataLayer({
        event: 'select_hotel_promotion',
        hotel_id: String(promo.CodigoHotel),
        hotel_name: promo.Hotel,
        promotion_name: promo.Nome,
        price_starting: promo.Tarifa.Valor,
      });
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'hotel',
            position: 0,
            label: 'Hotel',
            fields: [
              {
                name: 'hotel',
                type: 'VueSelect',
                label: 'Listar promoções do hotel',
                helpText: 'Selecione um hotel para ver as promoções',
                options() {
                  const hotelsToOptions = (hotels) =>
                    hotels.map((hotel) => ({
                      label: hotel.Nome,
                      value: hotel.Codigo,
                    }));

                  // Are hotels already loaded?
                  if (this.hotels.length > 0) {
                    return hotelsToOptions(this.hotels);
                  }

                  // THIS IS PURE MAGIC
                  return new Promise((resolve) => {
                    const unwatch = this.$watch('hotels', (hotels) => {
                      resolve(hotelsToOptions(hotels));
                      unwatch();
                    });
                  });
                },
              },
            ],
          },
          {
            name: 'text',
            fields: [
              {
                name: 'buttonText',
                type: 'ShortText',
                label: 'Botão',
                maxlength: 35,
              },
            ],
          },
          {
            name: 'config',
            label: 'Configurações',
            fields: [
              {
                name: 'orderBy',
                type: 'VueSelect',
                label: 'Ordenar promoções por:',
                options: [
                  {
                    label: 'Data de início',
                    value: 'initial-date',
                  },
                  {
                    label: 'Menor preço',
                    value: 'lowest-price',
                  },
                  {
                    label: 'Maior preço',
                    value: 'highest-price',
                  },
                  {
                    label: 'Maior desconto',
                    value: 'highest-discount',
                  },
                  {
                    label: 'Ordem aleatória',
                    value: 'random',
                  },
                ],
              },
              {
                name: 'showPrice',
                type: 'Checkbox',
                label: 'Mostrar preços',
              },
            ],
          },
          {
            name: 'nopromo',
            label: 'Aviso de promoção indisponível',
            helpText:
              'Defina uma mensagem que aparecerá para os seus hóspedes quando não houver nenhuma promoção cadastrada.',
            fields: [
              {
                name: 'customNoPromoTitle',
                type: 'ShortText',
                label: 'Título',
              },
              {
                name: 'customNoPromoSubtitle',
                type: 'LongText',
                label: 'Subtítulo',
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            toRemove: ['textColor'],
          },
          {
            name: 'fonts',
            toRemove: ['contentSizeDesk', 'contentSizeMobile'],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        discount: 'Até {count}% OFF',
        dailyRate: 'Diária a partir de',
        seePromotion: 'Ver promoção',
        seeMore: 'Ver mais',
        notAvailable: 'Ops, nossas promoções esgotaram!',
        notAvailableSubtitle:
          'Mas fique atento que em breve teremos novas ofertas para você aproveitar.',
      },
      en: {
        discount: 'Up to {count}% OFF',
        dailyRate: 'Room rates from',
        seePromotion: 'See promotion',
        seeMore: 'See more',
        notAvailable: 'Ops, our promotions are over!',
        notAvailableSubtitle:
          "Stay tuned that we'll soon have new offers for you.",
      },
      es: {
        discount: 'Hasta {count}% OFF',
        dailyRate: 'Tarifas a partir de',
        seePromotion: 'Ver promoción',
        seeMore: 'Ver más',
        notAvailable: 'Ops, nuestras promociones están agotadas.',
        notAvailableSubtitle:
          'Estén atentos que pronto tendremos nuevas ofertas para usted.',
      },
    },
  },
};
</script>

<style scoped lang="scss">
.promo-single {
  padding: 0 20px;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  .content {
    max-width: 1120px;

    .loader {
      margin-top: 30px;
      margin-bottom: 30px;
    }

    .lista-promocoes {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 32px;

      .bloco-promocao {
        display: flex;
        flex-wrap: wrap;
        background: $white;
        box-shadow: 0 3px 4px rgb(0 0 0 / 5%);
        min-height: 220px;
        position: relative;
        transition: transform 200ms;

        &:hover {
          transform: translateY(-10px);
        }

        .imagem,
        .informacoes {
          width: 50%; // resultado padrão: 272px
          height: 204px; // 272x204 -> 4:3
        }

        .imagem {
          display: flex;
          align-items: center;
          justify-content: center;
          aspect-ratio: 4 / 3;

          .empty-image {
            width: auto;
            height: auto;
          }
        }

        .promo-label {
          position: absolute;
          top: 0;
          left: 0;
          font-size: $size-small;
          font-weight: $lb-bold;
          line-height: 2.6;
          padding: 0 15px;
          text-transform: uppercase;
          color: $white;
          background-color: var(--promo-color);
        }

        .informacoes {
          display: flex;
          flex-direction: column;
          padding: 16px;

          .descricao,
          .tarifas {
            float: none;
            padding: 0;
            text-align: left;
            width: 100%;
          }

          .descricao {
            .header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding-bottom: 13px;
            }

            .nome {
              height: 42px;
              line-height: 21px; // 2 linhas
              max-width: 85%;
              margin: 0;
              color: $lb-dark-gray;
              text-align: left;
              font-weight: $lb-bold;
              text-transform: uppercase;

              -webkit-line-clamp: 2;
              overflow: hidden;
              display: -webkit-box;
              -webkit-box-orient: vertical;
            }

            .info-tooltip {
              cursor: pointer;
              margin-left: 5px;
              position: relative;

              .slb-tooltip {
                right: -10px;
                visibility: hidden;
                opacity: 0;
                color: $lb-dark-gray;
                left: unset;
                right: -15px;

                &.open-up,
                &.open-down {
                  opacity: 1;
                  visibility: visible;
                  transform: translateY(0);
                }

                .items-tooltip {
                  line-height: 1.8em;
                  text-align: left;
                }

                &.arrow-right-down {
                  &:after,
                  &:before {
                    left: 90%;
                  }
                }
              }
            }

            .promo-desc-container {
              height: 68px;
              position: relative;

              .features {
                line-height: 1.2;
                height: 100%;

                .descricaoPrincipal,
                .descricaoLastMinute {
                  line-height: 1.4;
                  font-size: $size-smaller;
                }
              }
            }
          }

          .tarifas {
            height: auto;
            margin-top: 8px;

            .diaria {
              margin-top: 0;
              font-size: 16px;

              .rotulo {
                line-height: 1;
              }

              .valor {
                font-size: 20px;
                font-weight: $lb-bold;
                margin: 0;
                display: block;
                line-height: 1;
              }

              .valor-promo {
                text-decoration: line-through;
              }
            }
          }
        }

        .cta {
          width: 100%;
          background-color: var(--button-color, var(--primary-color));

          &:hover {
            text-decoration: none;
          }
        }
      }
    }

    .cta--ver-mais {
      width: 200px;
      text-transform: uppercase;
      margin: 30px auto 0;
      color: var(--secondary-color);
      border-color: var(--secondary-color);
    }
  }
}

@include media-breakpoint-up(sm) {
  .promo-single {
    .informacoes {
      justify-content: space-between;
    }
  }
}

@include media-breakpoint-down(md) {
  .promo-single .content .lista-promocoes .bloco-promocao {
    max-width: 360px;

    .imagem,
    .informacoes {
      width: 100%;
    }

    .imagem {
      height: auto;
      aspect-ratio: 4/3;
    }
  }
}

@include media-breakpoint-down(sm) {
  .promo-single {
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);

    .content {
      .lista-promocoes {
        grid-template-columns: 1fr;
        place-items: center;

        .bloco-promocao .informacoes {
          height: auto;
        }
      }
    }
  }
}

@include media-breakpoint-only(md) {
  .promo-single {
    .content {
      .lista-promocoes {
        .bloco-promocao {
          .informacoes {
            .descricao {
              .nome {
                max-width: 78%;
                font-size: $size-small;
              }
            }
          }
        }
      }
    }
  }
}

.promo-empty-state {
  padding: 0;

  .container {
    padding: 0;
  }
}
</style>
